import React, { useState } from 'react'
import styled from '@emotion/styled'
import { CustomSelectOption } from 'types/index'
import { COLORS } from 'parameters/index'
import { useOutsideClickRef } from 'rooks'

interface IconProps {
	color: string
}

const ChevronIcon = ({ color }: IconProps): JSX.Element => (
	<svg xmlns="http://www.w3.org/2000/svg" width="10.828" height="6.414" viewBox="0 0 10.828 6.414">
		<path
			fill="none"
			stroke={color}
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="2px"
			d="M6 9l4 4 4-4"
			transform="translate(-4.586 -7.586)"
		/>
	</svg>
)

interface Props {
	options: CustomSelectOption[]
	initialValue?: string
	onChange: (option: CustomSelectOption) => void
}

export const Dropdown: React.FC<React.PropsWithChildren<Props>> = ({ options, initialValue, onChange }) => {
	const [isActive, setIsActive] = useState(false)
	const [selectedValue, setSelectedValue] = useState(initialValue || options[0].value)
	const selectedOption = options.find(o => o.value === selectedValue)!
	const [innerRef] = useOutsideClickRef(() => {
		setIsActive(false)
	})

	const handleSelection = (value: string): void => {
		setSelectedValue(value)
		setIsActive(false)
		onChange(options.find(x => x.value === value)!)
	}

	return (
		<Select data-testid="dropdown" ref={innerRef} className="dropDown">
			<Header onClick={(): void => setIsActive(!isActive)} className="selectHeader">
				<Text className="selectCurrentValue">{selectedOption.label}</Text>
				<Icon isActive={isActive} className="selectIcon">
					<ChevronIcon color={COLORS.BLACK} />
				</Icon>
			</Header>
			{isActive && (
				<SelectList className="selectList">
					{options.map(option => {
						return (
							<SelectItem
								key={option.value}
								onClick={(): void => handleSelection(option.value)}
								className="selectListItem"
							>
								{option.label}
							</SelectItem>
						)
					})}
				</SelectList>
			)}
		</Select>
	)
}

const Select = styled.div`
	width: 100%;
	display: flex;
	position: relative;
`

const Header = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	border-radius: 2px;
	cursor: pointer;
	border: solid 1px ${COLORS.PLATINUM};
	min-height: 46px;
	padding: 8px 17px;
`

const Text = styled.span`
	color: ${COLORS.JET_BLACK};
	font-size: 22px;
	font-weight: 800;
`

const Icon = styled.div<{ isActive: boolean }>`
	display: flex;
	margin-left: auto;

	svg {
		transform: ${({ isActive }): string => (isActive ? 'rotate(180deg)' : 'rotate(0deg)')};
		transition: 0.2s;
	}
`

const SelectList = styled.div`
	display: flex;
	position: absolute;
	top: 46px;
	display: flex;
	flex-direction: column;
	background-color: ${COLORS.WHITE};
	z-index: 5;
	width: 100%;
	padding: 16px 24px;
	box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.16);
`

const SelectItem = styled.div`
	font-size: 22px;
	font-weight: 800;
	padding: 12px 0;
	color: ${COLORS.JET_BLACK};
	cursor: pointer;

	:hover {
		color: ${COLORS.SAFETY_ORANGE};
	}
`
